interface FormatDateTimeOptions {
  fallback?: string;
  locale?: string | string[];
  options?: Intl.DateTimeFormatOptions;
}

/**
 * Format an ISO date string using Intl.DateTimeFormat.
 *
 * @param value - ISO date string or null/undefined.
 * @param fallback - Value returned when input is empty/invalid.
 * @param locale - Locale override (defaults to runtime locale).
 * @param options - Intl.DateTimeFormat options.
 * @returns Formatted date/time string.
 *
 * @example
 * formatDateTime('2024-01-02T03:04:05Z', { locale: 'en-US' })
 */
export function formatDateTime(
  value?: string | null,
  { fallback = '', locale, options }: FormatDateTimeOptions = {}
): string {
  if (!value) {
    return fallback;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  if (options) {
    return new Intl.DateTimeFormat(locale, options).format(date);
  }

  return date.toLocaleString(locale);
}

/**
 * Format an ISO date string as a date-only label.
 *
 * @param value - ISO date string or null/undefined.
 * @param fallback - Value returned when input is empty/invalid.
 * @param locale - Locale override (defaults to runtime locale).
 * @param options - Intl.DateTimeFormat options (merged with dateStyle).
 * @returns Date-only string.
 *
 * @example
 * formatDateOnly('2024-01-02T03:04:05Z', { locale: 'pt-BR' })
 */
export function formatDateOnly(
  value?: string | null,
  { fallback = '-', locale, options }: FormatDateTimeOptions = {}
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };
  return formatDateTime(value, {
    fallback,
    locale,
    options: { ...defaultOptions, ...(options ?? {}) },
  });
}

/**
 * Format an ISO date string as a time-only label.
 *
 * @param value - ISO date string or null/undefined.
 * @param fallback - Value returned when input is empty/invalid.
 * @param locale - Locale override (defaults to runtime locale).
 * @param options - Intl.DateTimeFormat options (merged with time fields).
 * @returns Time-only string.
 *
 * @example
 * formatTimeOnly('2024-01-02T03:04:05Z', { locale: 'en-US' })
 */
export function formatTimeOnly(
  value?: string | null,
  { fallback = '-', locale, options }: FormatDateTimeOptions = {}
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  return formatDateTime(value, {
    fallback,
    locale,
    options: { ...defaultOptions, ...(options ?? {}) },
  });
}

/**
 * Format a Date instance with Intl.DateTimeFormat.
 *
 * @param date - Date instance to format.
 * @param fallback - Value returned when input is invalid.
 * @param locale - Locale override (defaults to runtime locale).
 * @param options - Intl.DateTimeFormat options.
 * @returns Formatted date/time string.
 *
 * @example
 * formatDateFromDate(new Date('2024-01-02T03:04:05Z'), { locale: 'en-US' })
 */
export function formatDateFromDate(
  date: Date,
  { fallback = '', locale, options }: FormatDateTimeOptions = {}
): string {
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  if (options) {
    return new Intl.DateTimeFormat(locale, options).format(date);
  }
  return date.toLocaleString(locale);
}
